import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { usePayment } from '@/api/rpc/hooks/finance/usePayments';
import PaymentStatusBadge from '@/components/finance/PaymentStatusBadge';
import { format } from 'date-fns';
import { T } from 'gt-react';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { formatCurrency } from '@erp/shared-utils';

const DATE_FORMAT = 'MMM d, yyyy';

interface PaymentPreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

export function PaymentPreview({
  entityType,
  entityId,
  className,
}: PaymentPreviewProps) {
  const { payment } = usePayment(entityId);

  if (!payment) {
    return (
      <div className={cn('min-w-56 max-w-sm p-2', className)}>
        <p className="text-xs text-muted-foreground">
          <T>Loading…</T>
        </p>
      </div>
    );
  }

  const amountNumber = Number(payment.amount || 0);
  const amountAbsFormatted = formatCurrency(
    Math.abs(amountNumber),
    payment.currency || 'USD',
  );
  const formattedAmount =
    amountNumber < 0 ? `(${amountAbsFormatted})` : amountAbsFormatted;

  return (
    <div className={cn('min-w-56 max-w-sm p-2', className)}>
      <div className="flex items-center justify-between">
        <div className="font-mono font-semibold">{formattedAmount}</div>
        <PaymentStatusBadge status={payment.status} />
      </div>

      <div className="mt-1 text-xs text-muted-foreground">
        <span>
          {payment.paymentDate
            ? format(new Date(payment.paymentDate), DATE_FORMAT)
            : '—'}
        </span>
        <span className="mx-1.5">•</span>
        <span className="uppercase">{payment.method}</span>
        {payment.referenceNumber && (
          <>
            <span className="mx-1.5">•</span>
            <span className="font-mono">{payment.referenceNumber}</span>
          </>
        )}
      </div>

      {payment.memo && (
        <div className="mt-1 text-xs line-clamp-1">{payment.memo}</div>
      )}

      {((payment.invoices?.length ?? 0) > 0 ||
        payment.financialTransaction) && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-1.5">
            {payment.invoices?.slice(0, 2).map((inv) => (
              <SystemEntityInline
                key={inv.id}
                entityType={SystemEntity.AP_INVOICE}
                entityId={inv.id}
              />
            ))}
            {(payment.invoices?.length ?? 0) > 2 && (
              <span className="text-xs text-muted-foreground">
                +{(payment.invoices?.length ?? 0) - 2}
              </span>
            )}
            {payment.financialTransaction && (
              <SystemEntityInline
                entityType={SystemEntity.FINANCIAL_TRANSACTION}
                entityId={payment.financialTransaction.id}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
